// web/src/scene/CameraController.tsx
// M1.4 port of webview/src/scene/CameraController.tsx.
// Replaces eventBus with Redux selectors + SceneRefContext imperative API.
import { useEffect, useRef, useCallback } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import * as THREE from 'three'

import { useAppSelector } from '../app/useAppSelector'
import { useAppDispatch } from '../app/useAppDispatch'
import { viewerActions } from '@shared/slices/viewerSlice'
import { useSceneRef } from './SceneRefContext'
import type { PositionHistoryEntry, RTLSPosition } from '@shared/types/rtlsTypes'

// ---------------------------------------------------------------------------
// Chase-camera tuning constants (Google-Maps navigation-style follow)
// ---------------------------------------------------------------------------
const CHASE_FOLLOW_DISTANCE    = 150   // world units behind asset along heading
const CHASE_FOLLOW_HEIGHT      = 80    // world units above asset (Z-up scene)
const CHASE_LOOKAHEAD_DIST     = 40    // world units ahead of asset for camera lookAt
const CHASE_LOOKAHEAD_MS       = 250   // ms ahead to sample heading
const CHASE_HEADING_ALPHA      = 0.12  // EMA rate for smoothed heading
const CHASE_CAM_ALPHA          = 0.12  // camera position lerp rate per frame
const CHASE_TARGET_ALPHA       = 0.12  // lookAt target lerp rate per frame
const HEADING_MOVE_THRESHOLD   = 0.5   // below this, treat asset as stationary

// Reusable vectors — avoids per-frame allocation in the hot useFrame path.
const _desiredCamPos = new THREE.Vector3()
const _desiredLookAt = new THREE.Vector3()

// ---------------------------------------------------------------------------
// Module-level helper — mirrors usePlaybackPositions math without allocating
// a new Map per frame (avoids re-render cascade from CameraController).
// ---------------------------------------------------------------------------

function interpolateFromHistory(
  entries: PositionHistoryEntry[],
  ts: number,
): RTLSPosition | null {
  if (entries.length === 0) return null
  // Binary search for largest index with timestamp <= ts
  let lo = 0, hi = entries.length - 1, floorIdx = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (entries[mid].timestamp <= ts) {
      floorIdx = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  if (floorIdx < 0) return entries[0].position
  if (floorIdx === entries.length - 1) return entries[floorIdx].position
  const a = entries[floorIdx]
  const b = entries[floorIdx + 1]
  const span = b.timestamp - a.timestamp
  const t = span > 0 ? Math.min(1, Math.max(0, (ts - a.timestamp) / span)) : 0
  return {
    x: a.position.x + (b.position.x - a.position.x) * t,
    y: a.position.y + (b.position.y - a.position.y) * t,
    z: a.position.z + (b.position.z - a.position.z) * t,
  }
}

// ---------------------------------------------------------------------------
// Internal animation state shapes (unchanged from webview)
// ---------------------------------------------------------------------------

interface FlyToState {
  active: boolean
  startPosition: THREE.Vector3
  startTarget: THREE.Vector3
  endPosition: THREE.Vector3
  endTarget: THREE.Vector3
  progress: number
  duration: number
}

interface FollowState {
  active: boolean
  targetPosition: THREE.Vector3
  direction: THREE.Vector3
  followDistance: number
  followHeight: number
  currentCamTarget: THREE.Vector3
  initialFlyDone: boolean
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CameraController() {
  const controlsRef = useRef<any>(null)
  const { camera, gl } = useThree()
  const dispatch = useAppDispatch()
  const sceneRefHolder = useSceneRef()

  // Redux selectors replacing eventBus messages
  const viewConfig = useAppSelector(s => s.map.viewConfig)
  const interactionMode = useAppSelector(s => s.viewer.interactionMode)

  // Playback-follow: during playback with a selected asset, the camera
  // flies to and tracks the asset's interpolated position.
  const playbackMode = useAppSelector(s => s.rtls.playbackMode)
  const selectedAssetId = useAppSelector(s => s.rtls.selectedAssetId)
  const playbackFlownForRef = useRef<string | null>(null)

  // Refs for per-frame follow target update — avoids allocating a new Map
  // per frame (what usePlaybackPositions did) which would re-fire the follow
  // useEffect at 60 Hz.
  const playbackTs = useAppSelector(s => s.rtls.playbackTimestamp)
  const positionHistory = useAppSelector(s => s.rtls.positionHistory)
  const playbackSpeed = useAppSelector(s => s.rtls.playbackSpeed)
  const playbackTsRef = useRef(playbackTs)
  const positionHistoryRef = useRef(positionHistory)
  const selectedAssetIdRef = useRef(selectedAssetId)
  const playbackSpeedRef = useRef(playbackSpeed)
  useEffect(() => { playbackTsRef.current = playbackTs }, [playbackTs])
  useEffect(() => { positionHistoryRef.current = positionHistory }, [positionHistory])
  useEffect(() => { selectedAssetIdRef.current = selectedAssetId }, [selectedAssetId])
  useEffect(() => { playbackSpeedRef.current = playbackSpeed }, [playbackSpeed])

  // Saved position for reset()
  const savedCameraState = useRef<{
    position: THREE.Vector3
    target: THREE.Vector3
  } | null>(null)

  // Camera-change reporting
  const cameraReportTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const isDirty = useRef(false)

  // Last bounding box stored for fitToModel() re-use (no-arg call)
  const lastBoundingBox = useRef<THREE.Box3 | null>(null)

  // Fly-to animation state
  const flyToState = useRef<FlyToState>({
    active: false,
    startPosition: new THREE.Vector3(),
    startTarget: new THREE.Vector3(),
    endPosition: new THREE.Vector3(),
    endTarget: new THREE.Vector3(),
    progress: 0,
    duration: 1.2,
  })

  // Follow mode — internal only; NOT exposed on SceneRef in M1.
  const follow = useRef<FollowState>({
    active: false,
    targetPosition: new THREE.Vector3(),
    direction: new THREE.Vector3(0, 0, 1),
    followDistance: 100,
    followHeight: 60,
    currentCamTarget: new THREE.Vector3(),
    initialFlyDone: false,
  })

  // Chase-camera smoothed heading (XY unit vector). Default -Y so the
  // camera starts behind an asset that isn't moving yet. Seeded from the
  // first two trail entries on playback entry for a correct initial framing.
  const smoothHeading = useRef(new THREE.Vector2(0, -1))

  // ---------------------------------------------------------------------------
  // Z-up convention (mine CAD export)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    camera.up.set(0, 0, 1)
  }, [camera])

  // ---------------------------------------------------------------------------
  // Helpers (stable refs — close over camera / controlsRef via refs, not deps)
  // ---------------------------------------------------------------------------

  const saveCameraState = useCallback(() => {
    if (controlsRef.current) {
      savedCameraState.current = {
        position: camera.position.clone(),
        target: controlsRef.current.target.clone(),
      }
    }
  }, [camera])

  /** Internal startFlyTo — mirrors webview's startFlyTo exactly. */
  const startFlyTo = useCallback(
    (
      targetPos: THREE.Vector3,
      viewDistance: number,
      duration = 1.2,
      approachDir?: THREE.Vector3,
    ) => {
      if (!controlsRef.current) return

      const state = flyToState.current
      state.startPosition.copy(camera.position)
      state.startTarget.copy(controlsRef.current.target)

      // Compute end position: offset from target at viewDistance.
      //
      // Scene is Z-up. After AssetMarker's rotation.set(π/2, 0, 0) bake,
      // the GLB's glTF +Z (forward) maps to world -Y for equipment, so
      // camera at +Y side sees the front. Personnel GLBs have an opposite
      // forward axis — callers pass the negated approachDir for them.
      //
      // Default (equipment): camera on +Y with moderate lift.
      const viewDir = (approachDir ?? new THREE.Vector3(0, 0.85, 0.5)).clone().normalize()

      state.endTarget.copy(targetPos)
      state.endPosition.copy(targetPos).add(viewDir.multiplyScalar(viewDistance))

      state.progress = 0
      state.duration = duration
      state.active = true

      // Disable user interaction during animation
      if (controlsRef.current) {
        controlsRef.current.enableRotate = false
        controlsRef.current.enablePan = false
        controlsRef.current.enableZoom = false
      }
    },
    [camera],
  )

  // ---------------------------------------------------------------------------
  // Bootstrap: apply viewConfig once when it becomes non-null
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!viewConfig) return
    if (!controlsRef.current) return

    const { cameraPosition, cameraTarget } = viewConfig
    if (!cameraPosition || !cameraTarget) return

    camera.position.set(cameraPosition[0], cameraPosition[1], cameraPosition[2])
    controlsRef.current.target.set(cameraTarget[0], cameraTarget[1], cameraTarget[2])
    controlsRef.current.update()
    saveCameraState()
  // Only re-run when viewConfig identity changes (bootstrap fires once).
  // saveCameraState is stable (useCallback with [camera] dep).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewConfig])

  // ---------------------------------------------------------------------------
  // Sync interactionMode from Redux → OrbitControls
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!controlsRef.current) return
    const controls = controlsRef.current

    // Pan is ALWAYS enabled — right-click drag pans regardless of mode, so
    // the user can slide the map left/right/forward/back without flipping
    // to pan mode. Arrow keys also pan (keyPanSpeed default 7 units/press).
    // What the mode toggle changes is what LEFT-click drag does.
    controls.enablePan = true

    // Rotate reflects interactionMode only. During a chase, rotate stays
    // enabled so the user's left-drag can break out of follow (the capture-
    // phase pointerdown listener below flips follow.active off one frame
    // ahead of the first mouse-move — no jitter).
    if (interactionMode === 'orbit') {
      controls.enableRotate = true
      controls.mouseButtons = {
        LEFT: THREE.MOUSE.ROTATE,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.PAN,
      }
    } else {
      controls.enableRotate = false
      controls.mouseButtons = {
        LEFT: THREE.MOUSE.PAN,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.PAN,
      }
    }

    // Touch / trackpad: one-finger orbit, two-finger pan+zoom
    controls.touches = {
      ONE: THREE.TOUCH.ROTATE,
      TWO: THREE.TOUCH.DOLLY_PAN,
    }
  }, [interactionMode])

  // ---------------------------------------------------------------------------
  // Break out of chase on user rotate-drag.
  //
  // Capture phase so we fire before OrbitControls' own pointerdown handler —
  // by the time OrbitControls starts applying rotation, follow.active is
  // already false and the chase useFrame block short-circuits. Releasing
  // chase on the SAME pointerdown avoids a one-frame camera jitter that
  // would otherwise happen between chase's last placement and the user's
  // first mouse-move.
  //
  // Only left-button (button===0) breaks chase — right-drag (pan) and wheel
  // (zoom) leave chase active so the user can nudge without losing follow.
  // Once released, the user has manual control for the rest of playback;
  // re-attach by returning to live mode and re-entering playback.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const canvas = gl.domElement
    const handlePointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return
      if (!follow.current.active || !follow.current.initialFlyDone) return
      follow.current.active = false
      follow.current.initialFlyDone = false
    }
    canvas.addEventListener('pointerdown', handlePointerDown, true)
    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown, true)
    }
  }, [gl])

  // ---------------------------------------------------------------------------
  // Playback-follow: fly to selected asset on entry, then keep camera target
  // tracking the asset's interpolated playback position as scrubber advances.
  // Preserves orbit offset so user can still rotate while watching.
  // Per-frame target updates happen in useFrame via refs — this effect only
  // handles entry/exit transitions.
  // ---------------------------------------------------------------------------
  const assetsForFollow = useAppSelector(s => s.rtls.assets)
  useEffect(() => {
    // Exit condition — clear follow state and restore user controls.
    if (playbackMode !== 'playback' || !selectedAssetId) {
      // Remember whether chase was active BEFORE we tear it down so we can
      // decide whether to fly the camera to the asset's live position.
      const wasTracking = playbackFlownForRef.current

      follow.current.active = false
      follow.current.initialFlyDone = false
      playbackFlownForRef.current = null
      smoothHeading.current.set(0, -1)
      if (controlsRef.current) {
        controlsRef.current.enableRotate = interactionMode === 'orbit'
        controlsRef.current.enablePan    = true
        controlsRef.current.enableZoom   = true
      }

      // On natural playback completion (PlaybackAdvancer auto-flips mode to
      // 'live' at maxTs) the asset snaps to its LIVE position, but the
      // camera is still parked at the end of the trail — the asset can be
      // anywhere on the map. Fly the camera to catch up so the user doesn't
      // lose the asset. Only fires when we were actively tracking an asset
      // before this transition.
      if (wasTracking && selectedAssetId) {
        const asset = assetsForFollow[selectedAssetId]
        if (asset) {
          const type = asset.type
          const approachDir = type === 'personnel'
            ? new THREE.Vector3(0, -0.85, 0.5)
            : new THREE.Vector3(0, 0.85, 0.5)
          startFlyTo(
            new THREE.Vector3(asset.position.x, asset.position.y, asset.position.z),
            150,
            1.2,
            approachDir,
          )
        }
      }

      return
    }

    // Already tracking this asset — nothing to do on entry; useFrame handles
    // the continuous target updates via refs.
    if (playbackFlownForRef.current === selectedAssetId) return

    // New selection (or fresh entry into playback): seed follow state and
    // trigger fly-to. Use refs directly so we don't depend on playbackTs
    // (which changes every frame and would re-fire this effect 60 times/sec).
    const ts = playbackTsRef.current
    const history = positionHistoryRef.current
    const entries = selectedAssetId ? history[selectedAssetId] : undefined
    const pos = entries && ts != null
      ? interpolateFromHistory(entries, ts)
      : null
    if (!pos) return

    follow.current.active = true
    follow.current.initialFlyDone = false
    follow.current.targetPosition.set(pos.x, pos.y, pos.z)
    follow.current.currentCamTarget.set(pos.x, pos.y, pos.z)

    // Seed smoothHeading from the first two trail entries so the initial
    // chase frame is behind the asset (otherwise the default (0,-1) would
    // park the camera in an arbitrary world direction for ~1 s).
    if (entries && entries.length >= 2) {
      const a = entries[0].position
      const b = entries[1].position
      const sx = b.x - a.x
      const sy = b.y - a.y
      const sl = Math.sqrt(sx * sx + sy * sy)
      if (sl > 0.001) {
        smoothHeading.current.set(sx / sl, sy / sl)
      }
    }

    // Personnel GLBs face the opposite direction vs equipment — flip Y sign.
    const assetType = assetsForFollow[selectedAssetId]?.type
    const approachDir = assetType === 'personnel'
      ? new THREE.Vector3(0, -0.85, 0.5)
      : new THREE.Vector3(0, 0.85, 0.5)

    startFlyTo(new THREE.Vector3(pos.x, pos.y, pos.z), 150, 1.2, approachDir)
    playbackFlownForRef.current = selectedAssetId
  }, [playbackMode, selectedAssetId, startFlyTo, assetsForFollow, interactionMode])

  // ---------------------------------------------------------------------------
  // Expose imperative camera API on SceneRef
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fitToModel = (box?: THREE.Box3) => {
      if (!controlsRef.current) return

      const resolvedBox = box ?? lastBoundingBox.current
      if (!resolvedBox) return

      // Store for future no-arg calls
      lastBoundingBox.current = resolvedBox.clone()

      const center = new THREE.Vector3()
      resolvedBox.getCenter(center)

      const sizeVec = new THREE.Vector3()
      resolvedBox.getSize(sizeVec)

      // Compute camera distance to fit bounding box in frustum (Z-up)
      const fovRad = ((camera as THREE.PerspectiveCamera).fov * Math.PI) / 180
      const aspect =
        (camera as THREE.PerspectiveCamera).aspect ||
        window.innerWidth / window.innerHeight
      const distForHeight = sizeVec.y / 2 / Math.tan(fovRad / 2)
      const distForWidth = sizeVec.x / 2 / (Math.tan(fovRad / 2) * aspect)
      const distance = Math.max(distForHeight, distForWidth) * 1.15 // 15% padding

      // Z-up: camera above center
      camera.position.set(center.x, center.y, center.z + distance)
      controlsRef.current.target.copy(center)
      controlsRef.current.update()

      saveCameraState()
    }

    sceneRefHolder.current = {
      camera: {
        setMode(mode: 'orbit' | 'pan') {
          // The interactionMode useEffect above actually applies the
          // mouseButtons + enableRotate/enablePan config. This method
          // just nudges Redux — the effect sees the new mode and
          // reconfigures OrbitControls.
          dispatch(viewerActions.setInteractionMode(mode))
        },

        zoomIn(factor = 0.8) {
          if (!controlsRef.current) return
          camera.position.lerp(controlsRef.current.target, 1 - factor)
          controlsRef.current.update()
          isDirty.current = true
        },

        zoomOut(factor = 1.25) {
          if (!controlsRef.current) return
          const target = controlsRef.current.target
          const direction = camera.position.clone().sub(target)
          camera.position.copy(target).add(direction.multiplyScalar(factor))
          controlsRef.current.update()
          isDirty.current = true
        },

        reset() {
          if (savedCameraState.current && controlsRef.current) {
            camera.position.copy(savedCameraState.current.position)
            controlsRef.current.target.copy(savedCameraState.current.target)
            controlsRef.current.update()
            isDirty.current = true
          }
        },

        fitToModel(box?: THREE.Box3) {
          fitToModel(box)
        },

        flyTo(
          position: { x: number; y: number; z: number },
          viewDistance = 150,
          duration = 1.2,
          approachDir?: { x: number; y: number; z: number },
        ) {
          const pos = new THREE.Vector3(position.x, position.y, position.z)
          const dir = approachDir
            ? new THREE.Vector3(approachDir.x, approachDir.y, approachDir.z)
            : undefined
          startFlyTo(pos, viewDistance, duration, dir)
        },
      },
    }

    return () => {
      sceneRefHolder.current = null
    }
  // sceneRefHolder ref object is stable; dispatch, saveCameraState, startFlyTo are stable callbacks.
  // camera ref is also stable — useThree returns the same object.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, saveCameraState, startFlyTo])

  // ---------------------------------------------------------------------------
  // Per-frame animation (fly-to + follow mode)
  // ---------------------------------------------------------------------------
  useFrame((_, delta) => {
    const state = flyToState.current

    if (state.active) {
      state.progress += delta / state.duration

      if (state.progress >= 1) {
        state.progress = 1
        state.active = false

        camera.position.copy(state.endPosition)
        if (controlsRef.current) {
          controlsRef.current.target.copy(state.endTarget)
          // Restore user interaction. Rotate follows interactionMode only —
          // if chase is about to start, a left-drag will break out of it
          // (see the pointerdown listener), so no reason to pre-disable.
          controlsRef.current.enableRotate = interactionMode === 'orbit'
          controlsRef.current.enablePan = true
          controlsRef.current.enableZoom = true
          controlsRef.current.update()
        }
        if (follow.current.active) {
          follow.current.initialFlyDone = true
          follow.current.currentCamTarget.copy(state.endTarget)
        }
        isDirty.current = true
        return
      }

      const t = easeInOutCubic(state.progress)
      camera.position.lerpVectors(state.startPosition, state.endPosition, t)
      if (controlsRef.current) {
        controlsRef.current.target.lerpVectors(
          state.startTarget,
          state.endTarget,
          t,
        )
        controlsRef.current.update()
      }
      return
    }

    // ── Chase camera (Google-Maps navigation-style) ─────────────────────
    const f = follow.current
    if (!f.active || !f.initialFlyDone) return

    const assetId = selectedAssetIdRef.current
    const ts      = playbackTsRef.current
    if (!assetId || ts == null) return

    const entries = positionHistoryRef.current[assetId]
    if (!entries || entries.length < 2) return

    // 1. Interpolate current position.
    const posNow = interpolateFromHistory(entries, ts)
    if (!posNow) return

    // 2. Interpolate a look-ahead position (or extrapolate past trail end).
    const aheadTs = ts + CHASE_LOOKAHEAD_MS
    const lastTs  = entries[entries.length - 1].timestamp
    let posAhead: RTLSPosition

    if (aheadTs <= lastTs) {
      posAhead = interpolateFromHistory(entries, aheadTs) ?? posNow
    } else {
      const p1 = entries[entries.length - 2].position
      const p2 = entries[entries.length - 1].position
      const dt = entries[entries.length - 1].timestamp - entries[entries.length - 2].timestamp
      if (dt > 0) {
        const extMs = aheadTs - lastTs
        posAhead = {
          x: p2.x + ((p2.x - p1.x) / dt) * extMs,
          y: p2.y + ((p2.y - p1.y) / dt) * extMs,
          z: p2.z,          // never extrapolate Z — ramp character changes
        }
      } else {
        posAhead = posNow
      }
    }

    // Scale lerp rates with playback speed so the camera can keep up at
    // high speeds. At 1x we want the cinematic lerp; at 10x+ the camera must
    // snap to the truck's new position each frame or it lags behind the
    // interpolated asset motion, making the scene look slow.
    const speed = playbackSpeedRef.current
    const headingAlpha = Math.min(1, CHASE_HEADING_ALPHA * speed)
    const camAlpha     = Math.min(1, CHASE_CAM_ALPHA     * speed)
    const targetAlpha  = Math.min(1, CHASE_TARGET_ALPHA  * speed)

    // 3. EMA-smooth heading. Stationary-asset guard: if displacement is
    //    below threshold, hold the previous heading instead of drifting
    //    toward a zero-length vector.
    const rawDx  = posAhead.x - posNow.x
    const rawDy  = posAhead.y - posNow.y
    const rawLen = Math.sqrt(rawDx * rawDx + rawDy * rawDy)
    if (rawLen > HEADING_MOVE_THRESHOLD) {
      const nx = rawDx / rawLen
      const ny = rawDy / rawLen
      smoothHeading.current.x += (nx - smoothHeading.current.x) * headingAlpha
      smoothHeading.current.y += (ny - smoothHeading.current.y) * headingAlpha
    }
    const hLen = smoothHeading.current.length()
    if (hLen > 0.001) smoothHeading.current.divideScalar(hLen)

    const hx = smoothHeading.current.x
    const hy = smoothHeading.current.y

    // 4. Desired camera position: behind the asset along heading, lifted up.
    _desiredCamPos.set(
      posNow.x - hx * CHASE_FOLLOW_DISTANCE,
      posNow.y - hy * CHASE_FOLLOW_DISTANCE,
      posNow.z + CHASE_FOLLOW_HEIGHT,
    )

    // 5. Desired lookAt: slightly ahead of the asset so the viewer sees the
    //    road ahead rather than the asset blocking the view.
    _desiredLookAt.set(
      posNow.x + hx * CHASE_LOOKAHEAD_DIST,
      posNow.y + hy * CHASE_LOOKAHEAD_DIST,
      posNow.z,
    )

    // 6. Lerp both camera position and lookAt target with speed-scaled rates.
    camera.position.lerp(_desiredCamPos, camAlpha)
    if (controlsRef.current) {
      controlsRef.current.target.lerp(_desiredLookAt, targetAlpha)
      controlsRef.current.update()
    }
    isDirty.current = true
  })

  // ---------------------------------------------------------------------------
  // Camera-change reporter → Redux dispatch (replaces sendToNative CAMERA_CHANGED)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    cameraReportTimer.current = setInterval(() => {
      if (isDirty.current && controlsRef.current) {
        dispatch(
          viewerActions.setCameraState({
            position: {
              x: camera.position.x,
              y: camera.position.y,
              z: camera.position.z,
            },
            target: {
              x: controlsRef.current.target.x,
              y: controlsRef.current.target.y,
              z: controlsRef.current.target.z,
            },
          }),
        )
        isDirty.current = false
      }
    }, 500)

    return () => {
      if (cameraReportTimer.current) {
        clearInterval(cameraReportTimer.current)
      }
    }
  }, [camera, dispatch])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <OrbitControls
      ref={controlsRef}
      makeDefault
      enableDamping
      dampingFactor={0.1}
      enableRotate
      enablePan
      enableZoom
      // Trackpad pinch-zoom (macOS) fires high-rate wheel events and feels
      // jumpy at the OrbitControls default of 1.0. 0.5 dampens both trackpad
      // and mouse-wheel by half — trackpad gets the bigger win since each
      // pinch tick delivers a smaller delta.
      zoomSpeed={0.5}
      minDistance={10}
      maxDistance={5000}
      maxPolarAngle={Math.PI * 0.85}
      onChange={() => {
        isDirty.current = true
      }}
    />
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}
